import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ConfigService } from '../../../core/services/config/config.service';
import { UtilityService } from '../../../core/services/utility/utility.service';
import { User } from '../../models/user';

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  private utilityService = inject(UtilityService);
  private configService = inject(ConfigService);
  private config = this.configService.config;

  usersEndpoint = this.config.loginApiUrl + '/Users';

  /**
   * The silent-login probe that produces `userStatusTypeId` — deliberately NOT bounded by the client
   * timeout, unlike every other GET in the app.
   *
   * `authGuard` fails *open* on a null status: if this read fails, `user.effects` still sets
   * `isAuthComplete = true` and leaves the status null, and the guard falls through to
   * `isAuthenticated`, skipping both the Pending -> assign-patient and !Active -> unauthorized
   * redirects. That fail-open predates the client timeout and is reachable via a 5xx or a network
   * failure, but a 30s budget would make it *deterministically* triggerable by a merely slow login
   * API — turning a hang into a confident wrong answer the guard then acts on.
   *
   * Blocking is the safer failure here: an unbounded probe leaves `isAuthComplete` false, so the
   * guard's `filter(ready => ready)` never emits and navigation stalls rather than admitting an
   * unverified user. That is exactly how this path behaved before PBI 54129 bounded requests.
   *
   * Do not remove this without closing the fail-open in `authGuard` first — the two are a pair.
   */
  getUserByAzureId(azureId: string): Observable<User> {
    const url = this.usersEndpoint + '?azureId=' + azureId;
    return this.utilityService.getRequest<User>(url, { noTimeout: true });
  }

  getOrInsertUser(user: User): Observable<User> {
    return this.utilityService.postRequest<User>(this.usersEndpoint, user);
  }
}
